import React from "react";

import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

import { parseCalendarDate } from "./AccountPlansListTab";
import type { AccountPlanOverview, PlanFile } from "../../Types/AccountPlan";

/** White card used across the Upload step. */
export const PlanCard: React.FC<{
  title: string;
  subtitle?: React.ReactNode;
  /** Right-aligned header content (badge, coming-soon pill). */
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, aside, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      </div>
      {aside && <div className="shrink-0">{aside}</div>}
    </div>
    <div className="mt-3">{children}</div>
  </section>
);

/** Mono uppercase eyebrow for the right-column cards. */
export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-gray-400">
    {children}
  </div>
);

export const StatusChip: React.FC<{ file: PlanFile }> = ({ file }) => {
  const map: Record<
    string,
    { cls: string; label: string; Icon: React.ElementType }
  > = {
    processing: {
      cls: "bg-purple-50 text-[#8538d4]",
      label: "Processing",
      Icon: Loader2,
    },
    ready: {
      cls: "bg-emerald-50 text-emerald-700",
      label: "Ready",
      Icon: CheckCircle2,
    },
    partial: {
      cls: "bg-amber-50 text-amber-800",
      label: "Ready (partial)",
      Icon: AlertTriangle,
    },
    failed: {
      cls: "bg-red-50 text-red-700",
      label: "Failed",
      Icon: AlertTriangle,
    },
    rejected: {
      cls: "bg-red-50 text-red-700",
      label: "Needs review",
      Icon: AlertTriangle,
    },
    needs_review: {
      cls: "bg-amber-50 text-amber-800",
      label: "Waiting for review",
      Icon: AlertTriangle,
    },
  };
  const s = map[file.status] || map.failed;
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${s.cls}`}
      title={file.error || undefined}
    >
      <s.Icon
        className={`h-3 w-3 ${file.status === "processing" ? "animate-spin" : ""}`}
        aria-hidden
      />
      {s.label}
    </span>
  );
};

/** Can the slides be opened? A version the checks stopped or flagged can be opened
 * to look at it; only ready / partial ones can become current. */
export const isViewable = (f: PlanFile) =>
  f.status === "ready" ||
  f.status === "partial" ||
  f.status === "rejected" ||
  f.status === "needs_review";

/** When a version was ingested: parse time, falling back to upload time. */
export const ingestedAt = (f: PlanFile) => f.processed_at || f.created_at;

export const pageUnit = (f: PlanFile, n?: number | null) =>
  f.document_type === "pdf"
    ? n === 1
      ? "page"
      : "pages"
    : n === 1
      ? "slide"
      : "slides";

/** `YYYY-MM-DD` calendar date → "Oct 1" (no UTC shift). */
export const calendarShortDate = (value?: string | null) =>
  value
    ? parseCalendarDate(value).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : "—";

/** The account's AE by name, for "{name} (AE) owns the plan". */
export const aeName = (data: AccountPlanOverview) =>
  data.team.find(m => /^ae$|account exec/i.test(m.role))?.name || null;
